/**
 * NOTE ABOUT LOCATION FLICKER:
 * This page combines data from multiple queries that write Box.location with different
 * field selections. To avoid “flashing” user-visible fields (name/description/municipality/locality),
 * we rely on a custom Apollo merge policy (see AuthorizedApolloProvider.tsx) that preserves
 * existing non-empty strings when later writes omit or send empty values.
 *
 * If you add new user-facing fields on Location and see flicker, either:
 *   - include those fields in all queries that write Box.location, or
 *   - extend the merge’s `preferExistingIfEmpty` list.
 *
 * See more detailed comments in --> AuthorizedApolloProvider.tsx.
 */
import React, {useState} from 'react'
import {generatePath, Link as RouterLink, Route, Switch, useParams} from 'react-router-dom'
import {Tooltip} from '@mui/material'
import {useNotification} from '../../common/notifications/useNotification'
import {BoxLink} from '../../common/router_links/BoxLink'
import {Routes} from '../../common/Routes'
import {toDisplayDateTime} from '../../common/utils/dateUtils'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {Container} from '../../webui/Container'
import {IconButton} from '../../webui/IconButton'
import {Link} from '../../webui/Link'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {CopyIcon} from '../../webui/icons/CopyIcon'
import {CompartmentPage} from '../compartment/CompartmentPage'
import {useAllOrganisationsForBoxPage} from '../organisation/gql/useAllOrganisationsForBoxPage'
import {BoxPageBatteryReadings} from './BoxPageBatteryReadings'
import {CurrentStatusTable} from './current-status/CurrentStatusTable'
import {EditBoxDrawer} from './edit/EditBoxDrawer'
import {EditCompartmentsDrawer} from './edit/EditCompartmentsDrawer'
import {useBoxById} from './gql/useBoxById'
import {LocationTranslation} from './LocationTranslation'
import {BoxMap} from './map/BoxMap'
import {ProductChange} from './ProductChange'
import {RentalHistoryTable} from './rental-history-table/RentalHistoryTable'
import {contractTypeLabel} from '../../common/domain/contractType'
import {SUPPORTED_LOCATION_TRANSLATION_LANGUAGES} from '../location/translationLanguages'
import {useTranslatedLocation} from './utils/useTranslatedLocation'
import {describeOpeningWindow, isoToHHmmStockholm} from './utils/openingHours'

interface BoxPageProps {}

export const BoxPage: React.FC<BoxPageProps> = () => {
  const {id} = useParams<{id: string}>()
  const {showNotification} = useNotification()

  const {data: box, loading, error} = useBoxById({variables: {id}})
  const {data: allOrganisations, loading: orgsLoading, error: orgsError} = useAllOrganisationsForBoxPage({})
  const {tLocationDescription} = useTranslatedLocation()

  const [openEditBoxDrawer, setOpenEditBoxDrawer] = useState(false)
  const onCloseEditBox = () => setOpenEditBoxDrawer(false)
  const onOpenEditBox = () => setOpenEditBoxDrawer(true)

  const [openEditCompartmentsDrawer, setOpenEditCompartmentsDrawer] = useState(false)
  const onCloseEditCompartments = () => setOpenEditCompartmentsDrawer(false)
  const onOpenEditCompartments = () => setOpenEditCompartmentsDrawer(true)

  const lineStyle = {display: 'flex', alignItems: 'center'}
  const titleStyle = {fontWeight: 600}
  const valueStyle = {}

  const headingStyle = {
    color: '#014F59',
  }

  const onCopySerialNumber = async (serialNumber: string) => {
    await navigator.clipboard.writeText(serialNumber)
    showNotification({
      message: 'Serial number copied to clipboard',
      severity: 'success',
    })
  }

  if (loading) {
    return <LoadingSpinnerContainer />
  }
  if (error) {
    return <div>{error.message}</div>
  }

  if (!box) {
    return <div>Could not find box information.</div>
  }

  const organisationForBox = allOrganisations?.find((o) => o.boxes.some((b) => b.id === box.id)) ?? null

  const openStartTime = isoToHHmmStockholm(box.openStartTime)
  const openEndTime = isoToHHmmStockholm(box.openEndTime)
  const hasOpeningHours = Boolean(openStartTime) && Boolean(openEndTime)

  return (
    <Container>
      <Paper>
        <Box alignItems="flex-start" display="flex" flexDirection={{xs: 'column', md: 'row'}} justifyContent="space-between" p={2}>
          <Box>
            <BoxLink boxId={id} linkText={box.name} />
            {!box.active && (
              <Typography mt={2} color="error" variant="body2">
                <strong>This box is not active</strong> (not shown on the map for users)
              </Typography>
            )}
            {box.temporarilyClosed && (
              <Typography mt={2} color="#FF8C00" variant="body2">
                <strong>This box is "temporarily closed" (short-term maintenance closure)</strong>
              </Typography>
            )}
            {box.deprecated && (
              <Typography mt={2} color="warning.main" variant="body2">
                This box is deprecated and not for use anywhere
              </Typography>
            )}
            {box.location && (
              <Box mt={2}>
                <Typography variant="body2">
                  <span style={lineStyle}>
                    <div style={titleStyle}>Location Name:&nbsp;</div>
                    <div style={valueStyle}>{box.location.name}</div>
                  </span>
                </Typography>
                <Typography mt={0.5} variant="body2">
                  <span style={lineStyle}>
                    <div style={titleStyle}>Location Description (English):&nbsp;</div>
                    <div style={valueStyle}>{box.location.description}</div>
                  </span>
                </Typography>

                <Typography mt={0.5} variant="body2">
                  <span style={lineStyle}>
                    <div style={titleStyle}>City/Locality:&nbsp;</div>
                    <div style={valueStyle}>{box.location.locality}</div>
                  </span>
                </Typography>

                {hasOpeningHours && (
                  <Typography mt={0.5} variant="body2">
                    <span style={lineStyle}>
                      <div style={titleStyle}>Opening hours:&nbsp;</div>
                      <div style={valueStyle}>{describeOpeningWindow(openStartTime, openEndTime)}</div>
                    </span>
                  </Typography>
                )}

                <Typography mt={1} variant="body2">
                  <span style={lineStyle}>
                    <div style={titleStyle}>Contract type:&nbsp;</div>
                    <div style={valueStyle}>{contractTypeLabel(box.contractType ?? null)}</div>
                  </span>
                </Typography>

                <Typography mt={1} variant="body2">
                  <span style={lineStyle}>
                    <div style={titleStyle}>Organisation:&nbsp;</div>
                    <div style={valueStyle}>
                      {orgsError && '—'}
                      {orgsLoading && !orgsError && 'Loading…'}
                      {!orgsLoading && !orgsError && !organisationForBox && '—'}
                      {!orgsLoading && !orgsError && organisationForBox && (
                        <Link
                          component={RouterLink}
                          to={generatePath(Routes.ORGANISATION, {
                            id: organisationForBox.id,
                          })}
                          underline="hover"
                        >
                          {organisationForBox.name}
                        </Link>
                      )}
                    </div>
                  </span>
                </Typography>

                <Typography mt={1} variant="body2">
                  <span style={lineStyle}>
                    <div style={titleStyle}>Price Weight:&nbsp;</div>
                    <div style={valueStyle}>{box.priceWeight.name}</div>
                  </span>
                </Typography>

                <Box mt={1} display="flex" alignItems="center">
                  <Typography variant="body2">
                    <div style={titleStyle}>Color:</div>
                  </Typography>
                  <Box ml={1}>
                    <Box display={'flex'} alignItems={'center'}>
                      <Box bgcolor={box.color} height={16} mr={1} width={16} borderRadius={4} />
                      <Typography variant="body2">{'(' + box.color + ')'}</Typography>
                    </Box>
                  </Box>
                </Box>

                {box.sponsor && (
                  <Typography mt={1} variant="body2">
                    <span style={lineStyle}>
                      <div style={titleStyle}>Sponsored by:&nbsp;</div>
                      <Link
                        component={RouterLink}
                        to={generatePath(Routes.SPONSOR, {
                          id: box.sponsor.id,
                        })}
                        underline="hover"
                      >
                        <div style={valueStyle}>{box.sponsor.name}</div>
                      </Link>
                    </span>
                  </Typography>
                )}

                {box.initialActivationTime && (
                  <Typography mt={1} variant="body2">
                    <span style={lineStyle}>
                      <div style={titleStyle}>Initially Activated:&nbsp;</div>
                      <div style={valueStyle}>{toDisplayDateTime(box.initialActivationTime)}</div>
                    </span>
                  </Typography>
                )}

                <Box mt={1} display="flex" alignItems="center">
                  <Typography variant="body2">
                    <span style={lineStyle}>
                      <div style={titleStyle}>Serial Number:&nbsp;</div>
                      <div style={valueStyle}>{box.serialNumber}</div>
                    </span>
                  </Typography>

                  {box.serialNumber && (
                    <Tooltip title="Copy serial number to clipboard" placement="top">
                      <IconButton
                        aria-label="Copy serial number to clipboard"
                        onClick={() => onCopySerialNumber(box.serialNumber)}
                        size="small"
                        sx={{
                          ml: 0.5,
                          padding: 0.5,
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <span
                  style={{
                    ...headingStyle,
                    display: 'inline-block',
                    paddingTop: 28,
                  }}
                >
                  Description Translations
                </span>
                <span
                  style={{
                    fontSize: '0.75rem',
                    paddingBottom: 4,
                    marginTop: '0.5rem',
                    display: 'block',
                    fontStyle: 'italic',
                  }}
                >
                  Per-language descriptions shown to users in that language. The location's own translations take precedence; otherwise the bundled
                  translation catalog is consulted.
                </span>

                {SUPPORTED_LOCATION_TRANSLATION_LANGUAGES.map((lng) => (
                  <LocationTranslation key={lng} description={tLocationDescription(box.location, box.name, lng)} lng={lng} />
                ))}
              </Box>
            )}
          </Box>

          <Box mt={{xs: 3, md: 0}} display="flex" flexDirection="column" alignItems={{xs: 'stretch', md: 'flex-end'}}>
            <Button variant="contained" color="primary" onClick={onOpenEditBox} sx={{mb: 2, width: '100%'}}>
              Edit
            </Button>
            <Button variant="outlined" onClick={onOpenEditCompartments} sx={{width: '100%'}}>
              Edit Compartments
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box mt={2}>
        <Paper>
          <BoxMap
            boxMarkers={
              box.location.coordinates
                ? [
                    {
                      active: box.active,
                      color: box.color,
                      coordinates: box.location.coordinates,
                      id: box.id,
                      title: box.name,
                      locationName: box.location.name ?? '',
                      organisationName: organisationForBox?.name ?? '',
                    },
                  ]
                : []
            }
          />
        </Paper>
      </Box>

      <Box mt={2}>
        <Switch>
          <Route path={Routes.COMPARTMENT}>
            <CompartmentPage />
          </Route>
          <Route path="/">
            <Box my={2}>
              <Paper>
                <CurrentStatusTable boxId={box.id} boxName={box.name} compartments={box.compartments} />
              </Paper>
            </Box>
            <Box my={2}>
              <Paper>
                <RentalHistoryTable boxId={box.id} />
              </Paper>
            </Box>
            <Box my={2}>
              <Paper>
                <BoxPageBatteryReadings boxId={box.id} />
              </Paper>
            </Box>
            <Box my={2}>
              <Paper>
                <ProductChange boxId={box.id} />
              </Paper>
            </Box>
          </Route>
        </Switch>
      </Box>

      <EditBoxDrawer box={box} open={openEditBoxDrawer} onClose={onCloseEditBox} />
      <EditCompartmentsDrawer box={box} open={openEditCompartmentsDrawer} onClose={onCloseEditCompartments} />
    </Container>
  )
}
